<template>
  <div>
    <div class="row">
      <div class="column">
        <form-radio-button-list
          id="deliver-as-soon-as-possible"
          data-qa="deliver-as-soon-as-possible"
          title=""
          :options="doNotDeliverBeforeOptions"
          :model-value="doNotDeliverBeforeDateRadioValue"
          :disabled="disabled"
          @change="toggleDoNotDeliverBefore"
        >
        </form-radio-button-list>
      </div>

      <div class="datepicker-container">
        <date-picker
          v-show="doNotDeliverBeforeDateChecked"
          id="doNotDeliverBeforeDate"
          :disabled="disabled"
          :date="doNotDeliverBeforeDateComputed"
          :title="''"
          :min-date="defaultMinDate"
          :max-date="maxDate"
          :error-message="errorMessageDoNotDeliverBeforeDate"
          @change-date="changeDoNotDeliverBeforeDate"
        />
      </div>
    </div>
    <confirmation-prompt
      id="deliver-before-strange-prompt"
      :show="showDoNotDeliverBeforeStrangePrompt"
      :title="retrieveModalText('doNotDeliverBefore')"
      :info-text="dateIsStrangeInfoText"
      :cancel-button-show="false"
      @close="showDoNotDeliverBeforeStrangePrompt = false"
    >
    </confirmation-prompt>
  </div>
</template>

<script>
import moment from 'moment'
import AdditionalOrder from '@/mixins/additionalOrder.js'
import DatePicker from '@/components/generic/presentational/atoms/datePicker/DatePicker.vue'
import { mapGetters, mapState } from 'vuex'
import { useVuelidate } from '@vuelidate/core'
import { required, helpers } from '@vuelidate/validators'
import ConfirmationPrompt from '@/components/generic/presentational/molecules/confirmationPrompt/ConfirmationPrompt.vue'
import FormRadioButtonList from '@/components/generic/presentational/atoms/formRadioButtonList/FormRadioButtonList.vue'

export default {
  components: {
    DatePicker,
    ConfirmationPrompt,
    FormRadioButtonList,
  },
  mixins: [AdditionalOrder],
  validations() {
    const NON_DELIVERY = 'NON_DELIVERY'

    const isAfterMinDate = (value, options) => {
      const { type = '' } = options

      const minDate = type === NON_DELIVERY ? this.defaultMinDate : this.minDate

      return !helpers.req(value) || (value != 'INVALID' && value.isSameOrAfter(minDate, 'day'))
    }

    const isBeforeMaxDate = (value) => {
      const maxDate = this.maxDate

      return !helpers.req(value) || (value !== 'INVALID' && value.isSameOrBefore(maxDate, 'day'))
    }

    const isValidDate = (value) => {
      return !helpers.req(value) || moment.utc(value).isValid()
    }

    return {
      deliveryDate: {
        required,
        isAfterMinDate: (value) => isAfterMinDate(value, { type: '' }),
        isValidDate,
      },
      doNotDeliverBeforeDate: {
        isValidDate,
        isAfterMinDate: (value) => isAfterMinDate(value, { type: NON_DELIVERY }),
        isBeforeMaxDate,
      },
    }
  },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    isPurchaseOrder: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    return { v$: useVuelidate() }
  },
  data() {
    return {
      doNotDeliverBeforeDate: null,
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      showDoNotDeliverBeforeStrangePrompt: false,
    }
  },
  computed: {
    ...mapState({
      stateDeliveryDate(state, getters) {
        return getters[this.namespaceWithAdditionalOrder + '/deliveryDate']
      },
      expectedDeliveryDates(state, getters) {
        return getters[this.namespace + '/expectedDeliveryDates']
      },
    }),
    namespace() {
      return this.isPurchaseOrder ? 'purchaseOrder' : 'quote'
    },
    namespaceWithAdditionalOrder() {
      return this.isPurchaseOrder ? 'purchaseOrder' : this.quoteNamespace
    },
    doNotDeliverBeforeOptions() {
      return [
        {
          name: this.retrieveModalText('deliverAsSoonAsPossible'),
          value: 'deliverAsSoonAsPossible',
        },
        {
          name: this.retrieveModalText('doNotDeliverBefore'),
          value: 'doNotDeliverBefore',
        },
      ]
    },
    deliveryDate() {
      return this.stateDeliveryDate ? moment.utc(this.stateDeliveryDate) : null
    },
    doNotDeliverBeforeDateRadioValue() {
      return this.doNotDeliverBeforeDateChecked ? 'doNotDeliverBefore' : 'deliverAsSoonAsPossible'
    },
    doNotDeliverBeforeDateChecked() {
      return !!this.doNotDeliverBeforeDate
    },
    doNotDeliverBeforeDateComputed() {
      return this.doNotDeliverBeforeDate || this.deliveryDate
    },
    dateIsStrangeInfoText() {
      return 'De gekozen niet leveren voor datum verschilt meer dan 6 maanden met de actuele leverdatum is dit correct?'
    },
    errorMessageDoNotDeliverBeforeDate() {
      if (this.doNotDeliverBeforeDateChecked && this.v$.doNotDeliverBeforeDate.$anyDirty) {
        if (!this.v$.doNotDeliverBeforeDate.isValidDate.$response)
          return 'Vul een geldige niet leverdatum voor in (dd-mm-jjjj)'
        if (!this.v$.doNotDeliverBeforeDate.isAfterMinDate.$response)
          return 'De niet leverdatum voor moet vanaf vandaag plaatsvinden'
        if (!this.v$.doNotDeliverBeforeDate.isBeforeMaxDate.$response)
          return 'De niet leveren voor datum moet niet meer dan 2 jaar in de toekomst plaatsvinden'
      }
      return ''
    },
    defaultMinDate() {
      return moment.utc().startOf('day')
    },
    minDate() {
      return this.expectedDeliveryDateDealer
        ? moment.utc(this.expectedDeliveryDateDealer).startOf('day')
        : this.defaultMinDate
    },
    maxDate() {
      return moment.utc().add(2, 'years').startOf('day')
    },
    expectedDeliveryDateDealer() {
      return !this.allDatesSame && this.expectedDeliveryDates?.ExpectedDeliveryDateDealer
    },
    allDatesSame() {
      if (!this.expectedDeliveryDates) return true

      const expectedDeliveryDateValues = Object.values(this.expectedDeliveryDates)

      return expectedDeliveryDateValues.every((date, index) => {
        if (index > 0) {
          return moment.utc(date).isSame(moment.utc(expectedDeliveryDateValues[index - 1]), 'day')
        }
        return true
      })
    },
  },
  created() {
    this.$nextTick(() => {
      this.v$.deliveryDate.$touch()
    })

    const stateDoNotDeliverBeforeDate =
      this.$store.getters[`${this.namespaceWithAdditionalOrder}/doNotDeliverBeforeDate`]
    this.doNotDeliverBeforeDate = stateDoNotDeliverBeforeDate
      ? moment.utc(stateDoNotDeliverBeforeDate)
      : null
  },
  unmounted() {},
  methods: {
    checkDoNotDeliveryBeforeDateIsStrange() {
      if (!moment.utc(this.doNotDeliverBeforeDate).isValid()) return

      let monthDiff = moment
        .utc(this.doNotDeliverBeforeDate)
        .diff(moment.utc(this.deliveryDate), 'month')

      if (monthDiff >= 6 || monthDiff <= -6) {
        this.showDoNotDeliverBeforeStrangePrompt = true
      }
    },
    changeDoNotDeliverBeforeDate(value) {
      this.doNotDeliverBeforeDate = value
      this.v$.doNotDeliverBeforeDate.$touch()
      if (!this.v$.doNotDeliverBeforeDate.$error) {
        this.checkDoNotDeliveryBeforeDateIsStrange()
        this.$store.dispatch(
          `${this.namespace}/setDoNotDeliverBeforeDate`,
          value?.toJSON().slice(0, 19) // format to yyyy-MM-ddTHH:mm:ss
        )
      }
    },
    retrieveModalText(labelName) {
      return this.i18n.t(`/expected delivery date/${labelName}`)
    },
    toggleDoNotDeliverBefore(event) {
      let doNotDeliverBefore = event === 'doNotDeliverBefore'
      if (doNotDeliverBefore) {
        this.changeDoNotDeliverBeforeDate(this.deliveryDate)
      } else {
        this.changeDoNotDeliverBeforeDate(null)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.row {
  .datepicker-container {
    min-height: 52px;
    align-items: flex-end;
  }
}

.bold-label {
  font-weight: $font-weight-bold;
}
</style>
